import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSession } from '@/lib/session'
import { useI18n, translateError } from '@/i18n'

export const GOOGLE_CLIENT_ID = '899898110309-9kg0r9s4tiacp68t8l65jq8t0q59v4n3.apps.googleusercontent.com'

interface GoogleSignInButtonProps {
  onSuccess?: () => void
  onError?: (err: string) => void
  label?: string
}

export function GoogleSignInButton({ onSuccess, onError, label }: GoogleSignInButtonProps) {
  const { loginWithGoogle } = useSession()
  const { t } = useI18n()
  const [loading, setLoading] = useState(false)

  const clientId =
    (import.meta as unknown as { env: Record<string, string> }).env?.VITE_GOOGLE_CLIENT_ID ||
    GOOGLE_CLIENT_ID

  useEffect(() => {
    if (typeof window === 'undefined') return
    const win = window as unknown as {
      google?: {
        accounts?: {
          id?: {
            initialize: (cfg: unknown) => void
            prompt: () => void
          }
        }
      }
    }

    if (win.google?.accounts?.id && clientId) {
      win.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: { credential?: string }) => {
          if (response?.credential) {
            setLoading(true)
            try {
              await loginWithGoogle(response.credential)
              onSuccess?.()
            } catch (err) {
              onError?.(translateError(err, t, t('auth.googleFailed')))
            } finally {
              setLoading(false)
            }
          }
        },
      })
      // Trigger Google One-Tap non-intrusively
      win.google.accounts.id.prompt()
    }
  }, [clientId, loginWithGoogle, onSuccess, onError, t])

  const handleGoogleClick = async () => {
    setLoading(true)
    try {
      const win = typeof window !== 'undefined'
        ? (window as unknown as {
            google?: {
              accounts?: {
                id?: {
                  initialize: (cfg: unknown) => void
                  prompt: (cb?: (notif: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => void) => void
                }
              }
            }
          })
        : null

      if (win?.google?.accounts?.id && clientId) {
        win.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response: { credential?: string }) => {
            if (response?.credential) {
              await loginWithGoogle(response.credential)
              onSuccess?.()
            }
          },
        })
        win.google.accounts.id.prompt()
        setTimeout(() => setLoading(false), 2000)
        return
      }

      // Dev fallback credentials
      const name = 'Google User'
      const email = 'user@gmail.com'
      const googleId = 'google_sub_1092837465'
      await loginWithGoogle(undefined, { email, name, googleId })
      onSuccess?.()
    } catch (err: unknown) {
      const msg = translateError(err, t, t('auth.googleFailed'))
      onError?.(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      disabled={loading}
      onClick={handleGoogleClick}
      className="w-full flex items-center justify-center gap-2.5 h-11 border-border bg-background hover:bg-muted font-medium transition-all shadow-xs"
    >
      {loading ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <svg className="size-4 shrink-0" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
          />
        </svg>
      )}
      <span data-upbo-src="src/components/GoogleSignInButton.tsx:134">{label || t('auth.continueWithGoogle')}</span>
    </Button>
  )
}
